/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isEqual } from 'lodash';
import {
  createCriteria,
  updateCriteria,
  deleteCriteria,
  restoreCriteria,
  fetchCriteria,
  fulfillCriteria,
  unfulfillCriteria
} from '../../actions/criteriaActions';
import SourceTypes from '../../constants/sourceTypes';
import { WorkPackageStatuses } from '../../constants/storyStatusTypes';
import GrayPlus from '../Buttons/grayPlus';
import WhitePlus from '../Buttons/whitePlus';
import { allows } from '../../utils/authorization';
import expandIcon from '../../../assets/images/expandArrows.png';
import collapseIcon from '../../../assets/images/collapseArrows.png';

class AcceptanceCriteria extends React.Component {
  constructor(props) {
    super(props);

    this.state = { textInput: props.detailCard, acInput: '' };
  }

  componentDidMount() {
    if (
      this.props.workPackage.id > 0 &&
      !this.props.isNew &&
      this.props.detailCard
    ) {
      this.props.fetchCriteria(this.props.workPackage.id, 'accepted');
    }
  }

  focusInput = () => this.input && this.input.focus();

  handleChange = (event) => {
    this.setState({ acInput: event.target.value });
  };

  handleSubmit = (event) => {
    event.preventDefault();

    const { acInput, edit } = this.state;
    const { isNew, addCriteria, workPackage } = this.props;
    if (isNew) {
      if (!acInput) return null;
      addCriteria({
        type: 'accepted',
        short_name: acInput,
        tempId: edit
      });
      this.setState({ acInput: '', textInput: true, edit: undefined });
      return null;
    }

    const payload = {
      work_package_id: workPackage.id,
      type: 'accepted',
      criterion: {
        short_name: acInput
      }
    };

    if (payload.criterion.short_name.length > 0) {
      const editId = edit;
      this.setState({ acInput: '', edit: undefined }, () => this.focusInput());

      if (this.props.detailCard)
        this.setState({ acInput: '', textInput: true, edit: undefined }, () =>
          this.focusInput()
        );

      return editId
        ? this.props.updateCriteria(editId, payload, 'accepted')
        : this.props.createCriteria(payload, 'accepted');
    }
    this.focusInput();
    return null;
  };

  clearInputField = () => {
    this.setState({ acInput: '' }, () => this.focusInput());
  };

  handleFulfillCheck = (criterium) => {
    if (criterium.fulfilled) {
      this.props.unfulfillCriteria(criterium.id, 'accepted');
    } else {
      this.props.fulfillCriteria(criterium.id, 'accepted');
    }
  };

  handleEdit = ({ id, tempId, short_name: shortName }) => {
    this.setState({ textInput: true, edit: id || tempId }, () => {
      this.input.value = shortName;
      this.focusInput();
    });
  };

  handleDelete = (criterium, index) => {
    if (this.props.isNew) {
      this.props.removeCriteria(index);
      return;
    }
    this.props.deleteCriteria(criterium, 'accepted');
  };

  handleRestore = (criterium) => {
    this.props.restoreCriteria(criterium.id, 'accepted');
  };

  showTextInput = () => {
    this.setState({ textInput: true }, () => this.focusInput());
  };

  hideTextInput = () => {
    if (!this.state.acInput && this.props.closeBtn)
      this.setState({ textInput: false });
  };

  render() {
    const {
      criteria,
      index,
      workPackage,
      source,
      detailCard,
      isNew,
      showTrash,
      closeBtn,
      isCriteriaFull,
      toggleCriteriaFull,
      isFetching
    } = this.props;
    const { acInput, edit } = this.state;
    const allowEdit = allows('update', workPackage);
    const allowCreate = allows('create', workPackage);
    const allowDelete = allows('destroy', workPackage);
    const allowRestore = allows('restore_work_packages', workPackage);
    const gettingDoneWall = source === SourceTypes.COMPLETED;
    const isStartedNotAccepted =
      (workPackage.started && !workPackage.accepted) ||
      workPackage.state === WorkPackageStatuses.IN_PROGRESS ||
      workPackage.state === WorkPackageStatuses.COMPLETED;

    const isBeyondDevCompleted =
      workPackage.completed ||
      source === SourceTypes.COMPLETED ||
      source === SourceTypes.ACCEPTED ||
      source === SourceTypes.SHIPPING;

    const isAccepted =
      workPackage.accepted ||
      workPackage.state === WorkPackageStatuses.ACCEPTED ||
      workPackage.state === WorkPackageStatuses.SHIPPABLE ||
      workPackage.state === WorkPackageStatuses.LIVE;

    let divStyle = index % 2 === 0 ? 'gray-bg' : 'white-bg';
    if (detailCard)
      divStyle = `acceptance-criteria ${
        isCriteriaFull ? 'acceptance-criteria-full' : ''
      }`;

    return (
      <>
        {detailCard && (
          <label htmlFor="acceptance-criteria">Acceptance Criteria</label>
        )}
        <div className={divStyle}>
          {allowCreate && !gettingDoneWall && !detailCard && (
            <div className="add-task-button">
              {index % 2 === 0 ? (
                <WhitePlus handleClick={this.showTextInput} />
              ) : (
                <GrayPlus handleClick={this.showTextInput} />
              )}
            </div>
          )}
          <div
            className={`acceptance-criteria-list ${
              isCriteriaFull ? 'acceptance-criteria-list-full' : ''
            }`}
          >
            {detailCard && (
              <img
                src={isCriteriaFull ? collapseIcon : expandIcon}
                className={`resize-btn ${isCriteriaFull ? 'full' : ''}`}
                onClick={toggleCriteriaFull}
              />
            )}
            {criteria.map((criterium, ind) => (
              <div
                className={`criteria-row ${
                  criterium.deleted_at ? 'deleted' : ''
                }`}
                key={criterium.id || ind}
              >
                {showTrash && criterium.deleted_at && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => this.handleRestore(criterium, ind)}
                    disabled={!allowRestore}
                  >
                    <span className="glyphicon glyphicon-refresh" />
                  </button>
                )}
                {!isAccepted && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => this.handleDelete(criterium, ind)}
                    disabled={!allowDelete}
                  >
                    <span className="glyphicon glyphicon-minus red" />
                  </button>
                )}
                {!isBeyondDevCompleted && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => this.handleEdit(criterium)}
                    disabled={!allowEdit}
                  >
                    <span className="glyphicon glyphicon-pencil" />
                  </button>
                )}

                {!isNew && isStartedNotAccepted && (
                  <button
                    type="button"
                    className={`btn ${criterium.fulfilled && 'fulfilled'}`}
                    onClick={() => this.handleFulfillCheck(criterium)}
                    disabled={!allowEdit}
                  >
                    <span className="glyphicon glyphicon-ok" />
                  </button>
                )}
                <div className="criteria-name-container">
                  <span
                    className={`criteria-name ${
                      criterium.fulfilled && 'fulfilled'
                    }`}
                  >
                    {criterium.short_name}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {this.state.textInput && (
            <form
              className="form-inline acceptance-criteria-input"
              onSubmit={this.handleSubmit}
            >
              <span className="input-with-loading">
                <input
                  value={this.state.acInput}
                  disabled={!allowEdit}
                  onChange={this.handleChange}
                  ref={(input) => {
                    this.input = input;
                  }}
                  onBlur={this.hideTextInput}
                />
                {isFetching && <div id="add-story-mini-loader" />}
              </span>
              <button
                type="submit"
                className="btn btn-xs button-lavender"
                disabled={!allowEdit}
              >
                <strong>{edit ? 'SAVE' : 'ADD'} AC</strong>
              </button>
            </form>
          )}
        </div>
      </>
    );
  }
}

AcceptanceCriteria.propTypes = {
  detailCard: PropTypes.bool,
  source: PropTypes.string,
  workPackage: PropTypes.object,
  index: PropTypes.number,
  criteria: PropTypes.array,
  createCriteria: PropTypes.func,
  updateCriteria: PropTypes.func,
  deleteCriteria: PropTypes.func,
  fetchCriteria: PropTypes.func,
  fulfillCriteria: PropTypes.func,
  unfulfillCriteria: PropTypes.func
};

const mapStateToProps = (state, ownProps) => {
  if (!ownProps.isNew) {
    const criteria =
      state.criteriaReducer.criteria.accepted[ownProps.workPackage.id] || [];
    const { showTrash } = state.trashReducer;
    return {
      showTrash,
      criteria: showTrash
        ? criteria
        : criteria.filter((criterium) => !criterium.deleted_at),
      isFetching: state.criteriaReducer.isFetching
    };
  }
  return {};
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      createCriteria,
      updateCriteria,
      deleteCriteria,
      restoreCriteria,
      fetchCriteria,
      fulfillCriteria,
      unfulfillCriteria
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(AcceptanceCriteria);
